/*
 * Journey step: choose your net.
 *
 * The matcher, and the UI on top of it.
 *
 * Requirement comes from the measure step's spans. A net is laid along the
 * run and has to reach across the drop, so spans are grouped by how far
 * across they need to reach:
 *
 *   - The main run, plus any enclosed end no wider than it, share one net:
 *     run = the sum of their runs, drop = the main run's drop. The wider net
 *     already covers the narrower end, so joining them costs nothing.
 *   - An end WIDER than the main run gets its own net. Folding it into the
 *     main requirement used to force the entire run up to the end's width:
 *     a 30ft x 6ft alley with two 13ft x 13ft ends demanded 56ft of 17ft-wide
 *     net, when the alley itself only ever needed 6ft across.
 *
 * Every group must have a chosen net before the step completes. With one
 * group (the usual case) nothing changes from the single-net behaviour.
 *
 * A roll fits a group when one of its two orientations covers both the run
 * and the drop. This is why a 6' x 164' roll is not offered for a 30ft x 8ft
 * space: 6 cannot reach 8 either way around.
 *
 * Sold-out variants are filtered out before ranking, so the customer is only
 * ever shown sizes they can actually buy today.
 *
 * Allowances default to 0 on both the run and the drop, and should stay there.
 *
 * The measure step asks customers to round up to the nearest foot, so their
 * measurement already carries their own margin. Adding a percentage on top
 * double-counts it, and net widths are discrete, so even a small percentage
 * disqualifies the exact match: 10% of a 10ft drop demands 11ft, which rules
 * out the 10ft net and pushes the customer to a 17ft.
 *
 * The settings remain so the allowance can be dialled in if the install
 * reality calls for it, but they are opt-in rather than assumed.
 *
 * Candidates that fit are ranked by waste, so a 17' x 65' roll never surfaces
 * for a small space even though it technically fits.
 */
(function () {
  'use strict';

  function round(n, dp) {
    var f = Math.pow(10, dp === undefined ? 2 : dp);
    return Math.round(n * f) / f;
  }

  /*
   * Size grammars, one per unit the store can sell in.
   *
   * Metric is the AU original, tightened with a lookahead so "19mm" is not
   * read as 19 metres.
   *
   * Imperial has to be looser than you would hope, because US net titles are
   * not written to one convention. All of these are real, live titles:
   *
   *   "6' x 65'"               prime
   *   "10ft"                   the word, no space
   *   "4ft High x 16' Length"  both, in one string, with words between
   *   "11'4\""                 feet and inches
   *
   * and primes arrive as ASCII ' or as typographic ’ / ″ depending on whether
   * the title was typed or pasted from a spreadsheet. Group 2 is the optional
   * inches part.
   */
  var SIZE_PATTERNS = {
    m: /(\d+(?:\.\d+)?)\s*m(?![a-z])/gi,
    ft: /(\d+(?:\.\d+)?)\s*(?:['’′]|ft|feet)(?:\s*(\d+(?:\.\d+)?)\s*["”″])?/gi
  };

  /**
   * Parse a variant title into a size, in the unit the store sells in.
   *
   *   "6' x 65'" -> {a: 6,      b: 65}    (a roll)
   *   "10ft"     -> {a: 10,     b: null}  (a by-the-foot width)
   *   "11'4\""   -> {a: 11.333, b: null}  (feet and inches, as decimal feet)
   *
   * Returns null when the title is not a size.
   *
   * A null here is not an error, it means "this variant is not a size", and
   * the caller skips it. That is exactly why matching only "m", as the AU
   * original did, is dangerous on US: every net parses to null, every net is
   * skipped, and the customer is quietly told that nothing fits their space.
   * There is no error anywhere. Keep this function and its tests honest.
   */
  function parseSize(title, units) {
    if (!title) return null;

    var re = SIZE_PATTERNS[units === 'm' ? 'm' : 'ft'];
    re.lastIndex = 0; // shared regex objects carry state between calls

    var nums = [];
    var match;

    while ((match = re.exec(String(title))) !== null) {
      var n = parseFloat(match[1]);
      if (match[2]) n += parseFloat(match[2]) / 12;
      if (isFinite(n) && n > 0) nums.push(round(n, 3));
      if (nums.length >= 2) break;
    }

    if (!nums.length) return null;

    return { a: nums[0], b: nums.length > 1 ? nums[1] : null };
  }

  /** Does a rectangle cover the requirement in either orientation? */
  function covers(w, h, run, drop) {
    return (w >= run && h >= drop) || (h >= run && w >= drop);
  }

  function Netting(root) {
    this.root = root;
    this.config = this.readConfig();
    if (!this.config) return;

    this.stepId = this.config.stepId || 'netting';
    this.measureStepId = this.config.measureStepId || 'measure';
    this.labels = this.config.labels || {};

    // 'ft' on the US store, 'm' on AU. Drives both the size grammar and every
    // unit shown on screen, so the two can never drift apart.
    this.units = this.config.units === 'm' ? 'm' : 'ft';
    this.unit = this.units === 'm' ? 'm' : 'ft';
    this.areaUnit = this.units === 'm' ? 'm&sup2;' : 'ft&sup2;';

    this.introEl = root.querySelector('[data-net-intro]');
    this.coloursEl = root.querySelector('[data-net-colours]');
    this.formatsBlock = root.querySelector('[data-net-formats-block]');
    this.formatsEl = root.querySelector('[data-net-formats]');
    this.noneEl = root.querySelector('[data-net-none]');
    this.zipBlock = root.querySelector('[data-net-zip-block]');
    this.zipsEl = root.querySelector('[data-net-zips]');
    this.zipNoteEl = root.querySelector('[data-net-zip-note]');

    this.colour = null;
    this.choices = {}; // one chosen net per requirement group, keyed by group
    this.zip = null;
    this.journey = null;
    this.requirement = null;

    this.money = this.moneyFormatter();

    this.bind();
  }

  Netting.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-net-config]');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      return null;
    }
  };

  Netting.prototype.moneyFormatter = function () {
    var currency = this.config.currency || 'USD';

    try {
      // Pinned to the store's locale, not the visitor's. Left as undefined,
      // a visitor browsing from outside the US sees "USD 199.50" where the
      // rest of the storefront says "$199.50", because Intl falls back to a
      // currency code when the locale and currency disagree.
      var fmt = new Intl.NumberFormat(this.config.locale || 'en-US', { style: 'currency', currency: currency });
      return function (cents) {
        return fmt.format(cents / 100);
      };
    } catch (err) {
      return function (cents) {
        return '$' + (cents / 100).toFixed(2);
      };
    }
  };

  Netting.prototype.bind = function () {
    var self = this;

    if (window.CatnetsJourney) {
      this.attach(window.CatnetsJourney);
    } else {
      document.addEventListener('journey:ready', function (event) {
        self.attach(event.detail.journey);
      });
    }

    document.addEventListener('journey:step:enter', function (event) {
      if (event.detail.step === self.stepId) self.refresh();
    });

    // Measurements changed underneath us, so any existing choice may no longer
    // fit. Recompute rather than carry a stale recommendation forward.
    document.addEventListener('journey:step:complete', function (event) {
      if (event.detail.step === self.measureStepId) self.refresh();
    });
  };

  Netting.prototype.attach = function (journey) {
    this.journey = journey;

    var saved = journey.data(this.stepId);
    if (saved) {
      this.colour = saved.colour;
      this.zip = saved.zip || null;

      // Newer journeys carry a nets array; older saved ones are a single net
      // at the top level. Either way, each restored net is re-validated
      // against the current groups by the refresh below.
      var nets = saved.nets && saved.nets.length ? saved.nets : [saved];
      this.choices = {};
      for (var i = 0; i < nets.length; i++) {
        this.choices[nets[i].group || 'main'] = nets[i];
      }
    }

    this.refresh();
  };

  /**
   * Turn the measure step's spans into run-and-drop requirement groups,
   * one group per net the customer needs.
   */
  Netting.prototype.buildRequirement = function () {
    if (!this.journey) return null;

    var measure = this.journey.data(this.measureStepId);
    if (!measure || !measure.spans || !measure.spans.length) return null;

    // A Complete Wall Net panel IS the end's netting, zip included. Counting
    // that end's span as well would sell the same netting twice, so each
    // panel takes one enclosed end out of the requirement, in order.
    var spans = measure.spans;
    var panelCoversEnd = 0;

    if (this.zip && this.zip.tier === 'panel' && measure.end1) {
      var panelsLeft = this.zip.quantity || 1;
      var kept = [];
      for (var s = 0; s < spans.length; s++) {
        if (panelsLeft > 0 && spans[s].label && spans[s].label.indexOf('End') === 0) {
          panelsLeft--;
          panelCoversEnd++;
          continue;
        }
        kept.push(spans[s]);
      }
      spans = kept;
    }

    var runPct = typeof this.config.allowancePct === 'number' ? this.config.allowancePct : 10;
    var dropPct = typeof this.config.dropAllowancePct === 'number' ? this.config.dropAllowancePct : runPct;

    // Split the spans: anything the main run's width already reaches across
    // joins the main net; a wider end needs its own.
    var mainDrop = spans[0].drop;
    var mainSpans = [];
    var wideSpans = [];

    for (var i = 0; i < spans.length; i++) {
      (spans[i].drop > mainDrop ? wideSpans : mainSpans).push(spans[i]);
    }

    var groups = [];

    function addGroup(key, list) {
      if (!list.length) return;

      var run = 0;
      var drop = 0;
      var area = 0;
      var labels = [];

      for (var g = 0; g < list.length; g++) {
        run += list[g].run;
        area += list[g].area;
        if (list[g].drop > drop) drop = list[g].drop;
        labels.push(list[g].label);
      }

      groups.push({
        key: key,
        label: labels.join(' + '),
        spans: list,
        run: round(run * (1 + runPct / 100)),
        rawRun: round(run),
        drop: round(drop * (1 + dropPct / 100)),
        rawDrop: round(drop),
        area: round(area)
      });
    }

    addGroup('main', mainSpans);
    addGroup('ends', wideSpans);

    // Combined totals, for the intro line.
    var totalRun = 0;
    var maxDrop = 0;
    var totalArea = 0;

    for (var t = 0; t < spans.length; t++) {
      totalRun += spans[t].run;
      totalArea += spans[t].area;
      if (spans[t].drop > maxDrop) maxDrop = spans[t].drop;
    }

    return {
      measure: measure,
      spans: spans,
      groups: groups,
      panelCoversEnd: panelCoversEnd,
      run: round(totalRun * (1 + runPct / 100)),
      rawRun: round(totalRun),
      drop: round(maxDrop * (1 + dropPct / 100)),
      rawDrop: round(maxDrop),
      area: round(totalArea),
      allowancePct: runPct,
      dropAllowancePct: dropPct
    };
  };

  /**
   * Candidate nets for one product against one requirement group, ranked by
   * waste.
   * @returns {Array<object>}
   */
  Netting.prototype.candidates = function (product, req) {
    var out = [];

    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      var size = parseSize(variant.title, this.units);
      if (!size) continue;

      var candidate = null;

      if (product.format === 'btm') {
        // A by-the-foot variant is a width. It is the drop, and you buy as
        // many feet of run as you need.
        var width = size.a;
        if (width < req.drop) continue;

        var lengths = Math.ceil(req.run);

        candidate = {
          variantId: variant.id,
          sku: variant.sku,
          variantTitle: variant.title,
          available: variant.available,
          unitPrice: variant.price,
          quantity: lengths,
          totalPrice: variant.price * lengths,
          suppliedRun: lengths,
          suppliedDrop: width,
          wasteFt2: round(width * lengths - req.area)
        };
      } else {
        if (size.b === null) continue;
        if (!covers(size.a, size.b, req.run, req.drop)) continue;

        candidate = {
          variantId: variant.id,
          sku: variant.sku,
          variantTitle: variant.title,
          available: variant.available,
          unitPrice: variant.price,
          quantity: 1,
          totalPrice: variant.price,
          suppliedRun: Math.max(size.a, size.b),
          suppliedDrop: Math.min(size.a, size.b),
          wasteFt2: round(size.a * size.b - req.area)
        };
      }

      if (candidate) out.push(candidate);
    }

    // Least waste first. Price breaks ties, because two sizes can waste the
    // same amount at different prices.
    out.sort(function (a, b) {
      if (a.wasteFt2 !== b.wasteFt2) return a.wasteFt2 - b.wasteFt2;
      return a.totalPrice - b.totalPrice;
    });

    return out;
  };

  /** A colour is only usable when every group can be covered in it. */
  Netting.prototype.coloursWithStock = function (req) {
    var available = {};

    for (var c = 0; c < this.config.colours.length; c++) {
      var colour = this.config.colours[c].id;
      var coversAll = true;

      for (var g = 0; g < req.groups.length; g++) {
        var any = false;

        for (var i = 0; i < this.config.products.length; i++) {
          var product = this.config.products[i];
          if (product.colour !== colour) continue;

          var fits = this.candidates(product, req.groups[g]).filter(function (x) {
            return x.available;
          });

          if (fits.length) {
            any = true;
            break;
          }
        }

        if (!any) {
          coversAll = false;
          break;
        }
      }

      if (coversAll) available[colour] = true;
    }

    return available;
  };

  Netting.prototype.refresh = function () {
    if (!this.journey) return;

    this.requirement = this.buildRequirement();

    if (!this.requirement) {
      this.renderIntro(null);
      return;
    }

    this.validateSelections();
    this.renderIntro(this.requirement);
    this.renderColours();
    this.renderFormats();
    this.renderZips();
    this.report();
  };

  /** Every requirement group has a chosen net. */
  Netting.prototype.allChosen = function () {
    if (!this.requirement || !this.requirement.groups.length) return false;

    for (var g = 0; g < this.requirement.groups.length; g++) {
      if (!this.choices[this.requirement.groups[g].key]) return false;
    }
    return true;
  };

  /**
   * Measurements can change underneath a made choice (back to Measure, edit,
   * continue). The rule across the journey: a step resets only when its
   * selection is actually invalidated. So each chosen net is re-tested against
   * its group's new requirement: still fits, keep it and refresh quantity,
   * price and waste (by-the-foot lengths track the run); no longer fits, sold
   * out, or its group no longer exists, clear it, which un-ticks the step and
   * blocks Continue until re-chosen. Same for the zip: a fixed-size panel can
   * stop fitting a grown end.
   */
  Netting.prototype.validateSelections = function () {
    var req = this.requirement;

    var groupsByKey = {};
    for (var g = 0; g < req.groups.length; g++) {
      groupsByKey[req.groups[g].key] = req.groups[g];
    }

    for (var key in this.choices) {
      var choice = this.choices[key];
      var group = groupsByKey[key];
      var fresh = null;

      if (group) {
        for (var i = 0; i < this.config.products.length; i++) {
          var product = this.config.products[i];
          if (product.handle !== choice.productHandle) continue;

          var fits = this.candidates(product, group);
          for (var c = 0; c < fits.length; c++) {
            if (String(fits[c].variantId) === String(choice.variantId) && fits[c].available) {
              fresh = fits[c];
              break;
            }
          }
        }
      }

      if (fresh) {
        choice.group = key;
        choice.groupLabel = group.label;
        choice.quantity = fresh.quantity;
        choice.unitPrice = fresh.unitPrice;
        choice.totalPrice = fresh.totalPrice;
        choice.covers = { run: fresh.suppliedRun, drop: fresh.suppliedDrop };
        choice.required = { run: group.run, drop: group.drop, area: group.area };
        choice.wasteFt2 = fresh.wasteFt2;
      } else {
        delete this.choices[key];
      }
    }

    if (this.zip) {
      var stillOffered = false;
      var options = this.zipOptions().options;
      for (var z = 0; z < options.length; z++) {
        if (String(options[z].variantId) === String(this.zip.variantId)) stillOffered = true;
      }
      if (!stillOffered) {
        var wasPanel = this.zip.tier === 'panel';
        this.zip = null;

        // A dropped panel had been covering an end's netting; put that end
        // back into the requirement and re-test the chosen nets against it.
        if (wasPanel) {
          this.requirement = this.buildRequirement();
          this.validateSelections();
        }
      } else if ((this.zip.quantity || 1) > this.zipMax(this.zip)) {
        // Fewer ends than before: the zip quantity clamps down, and panels
        // give their freed end back to the netting requirement.
        this.zip.quantity = this.zipMax(this.zip);
        if (this.zip.tier === 'panel') {
          this.requirement = this.buildRequirement();
          this.validateSelections();
        }
      }
    }
  };

  /**
   * How many of a zip can go into this build. A panel replaces one end's
   * netting, so panels cap at the number of enclosed ends; zips fitted into
   * your own netting are a door anywhere, so they get a loose cap.
   */
  Netting.prototype.zipMax = function (zip) {
    if (zip.tier !== 'panel') return 4;

    var measure = this.requirement && this.requirement.measure;
    var ends = 0;
    if (measure) {
      if (measure.end1) ends++;
      if (measure.end2) ends++;
    }
    return Math.max(ends, 1);
  };

  Netting.prototype.renderIntro = function (req) {
    if (!this.introEl) return;

    if (!req) {
      this.introEl.hidden = true;
      return;
    }

    var unit = this.unit;
    var parts = req.spans.map(function (s) {
      return s.run + unit + ' &times; ' + s.drop + unit;
    });

    this.introEl.innerHTML =
      'Matching to ' + parts.join(' plus ') +
      '. That is ' + req.rawRun + unit + ' of run, and we need to reach ' + req.rawDrop + unit + ' across, ' +
      'plus a little spare on every edge to fix against.' +
      (req.groups.length > 1
        ? ' Your ends are wider than the main run, so they get their own net rather than forcing the whole thing wider.'
        : '') +
      (req.panelCoversEnd
        ? (req.panelCoversEnd > 1
          ? ' Your Complete Wall Nets cover the enclosed ends, so their netting is not counted here.'
          : ' Your Complete Wall Net covers the enclosed end, so its netting is not counted here.')
        : '');
    this.introEl.hidden = false;
  };

  Netting.prototype.renderColours = function () {
    if (!this.coloursEl) return;

    var stocked = this.coloursWithStock(this.requirement);
    var html = '';

    for (var i = 0; i < this.config.colours.length; i++) {
      var colour = this.config.colours[i];
      var usable = !!stocked[colour.id];
      var selected = this.colour === colour.id;

      // An uploaded image gets a proper photo tile; a bare colour stays a dot.
      var swatch = colour.image
        ? '<span class="jnet__swatch jnet__swatch--photo" style="background-image:url(' + colour.image + ')"></span>'
        : '<span class="jnet__swatch" style="background:' + (colour.swatch || '#ccc') + '"></span>';

      html +=
        '<button type="button" class="jnet__colour' + (selected ? ' is-selected' : '') + '"' +
        ' role="radio" aria-checked="' + (selected ? 'true' : 'false') + '"' +
        ' data-net-colour="' + colour.id + '"' +
        (usable ? '' : ' disabled aria-disabled="true"') + '>' +
        swatch +
        '<span class="jnet__colour-label">' + colour.label + '</span>' +
        (usable ? '' : '<span class="jnet__colour-note">' + (this.labels.soldOut || 'Unavailable') + '</span>') +
        '</button>';
    }

    this.coloursEl.innerHTML = html;

    var self = this;
    var buttons = this.coloursEl.querySelectorAll('[data-net-colour]');
    for (var b = 0; b < buttons.length; b++) {
      buttons[b].addEventListener('click', function (event) {
        self.selectColour(event.currentTarget.getAttribute('data-net-colour'));
      });
    }
  };

  Netting.prototype.selectColour = function (id) {
    if (this.colour === id) return;

    this.colour = id;

    // The chosen nets and zip belong to the old colour, so drop them.
    this.choices = {};
    this.zip = null;

    this.renderColours();
    this.renderFormats();
    this.renderZips();
    this.report();
  };

  Netting.prototype.renderFormats = function () {
    if (!this.formatsEl || !this.formatsBlock) return;

    if (!this.colour) {
      this.formatsBlock.hidden = true;
      return;
    }

    this.formatsBlock.hidden = false;

    var groups = this.requirement.groups;
    var multi = groups.length > 1;
    var max = typeof this.config.maxAlternatives === 'number' ? this.config.maxAlternatives : 3;
    var html = '';
    var any = false;

    for (var g = 0; g < groups.length; g++) {
      var group = groups[g];
      var chosenNet = this.choices[group.key];
      var groupHtml = '';

      for (var i = 0; i < this.config.products.length; i++) {
        var product = this.config.products[i];
        if (product.colour !== this.colour) continue;

        var fits = this.candidates(product, group).filter(function (c) {
          return c.available;
        });

        if (!fits.length) continue;

        any = true;

        var best = fits[0];
        var others = fits.slice(1, 1 + max);
        var chosen = chosenNet && String(chosenNet.variantId) === String(best.variantId) &&
          chosenNet.productHandle === product.handle;

        groupHtml +=
          '<div class="jnet__format' + (chosen ? ' is-chosen' : '') + '">' +
            (product.image ? '<img class="jnet__format-image" src="' + product.image + '" alt="" loading="lazy">' : '') +
            '<div class="jnet__format-head">' +
              '<span class="jnet__format-name">' + (product.formatLabel || product.title) + '</span>' +
              '<span class="jnet__badge">' + (this.labels.recommended || 'Best match') + '</span>' +
            '</div>' +
            (product.blurb ? '<p class="jnet__format-blurb">' + product.blurb + '</p>' : '') +
            this.optionHtml(product, best, true, group) +
            (others.length ? this.othersHtml(product, others, group) : '') +
          '</div>';
      }

      if (multi) {
        html +=
          '<div class="jnet__net-group" data-net-group-key="' + group.key + '">' +
            '<h4 class="jnet__net-title">' +
              'Net ' + (g + 1) + ': ' + group.label +
              '<span class="jnet__net-size">' + group.rawRun + this.unit + ' &times; ' + group.rawDrop + this.unit + '</span>' +
            '</h4>' +
            '<div class="jnet__net-options">' +
              (groupHtml || '<p class="jnet__none">' + (this.noneEl ? this.noneEl.textContent : '') + '</p>') +
            '</div>' +
          '</div>';
      } else {
        html += groupHtml;
      }
    }

    this.formatsEl.innerHTML = html;
    if (this.noneEl) this.noneEl.hidden = any;

    this.bindOptionButtons();
  };

  Netting.prototype.optionHtml = function (product, candidate, isBest, group) {
    var chosenNet = this.choices[group.key];
    var chosen = chosenNet && String(chosenNet.variantId) === String(candidate.variantId) &&
      chosenNet.productHandle === product.handle;

    var qty = product.format === 'btm'
      ? candidate.quantity + this.unit + ' of ' + candidate.variantTitle + ' wide'
      : candidate.variantTitle;

    var unit = product.format === 'btm'
      ? this.money(candidate.unitPrice) + ' ' + (this.labels.perUnit || 'per foot')
      : '';

    return (
      '<div class="jnet__option' + (chosen ? ' is-chosen' : '') + (isBest ? ' jnet__option--best' : '') + '">' +
        '<div class="jnet__option-main">' +
          '<span class="jnet__option-size">' + qty + '</span>' +
          (unit ? '<span class="jnet__option-unit">' + unit + '</span>' : '') +
          '<span class="jnet__option-waste">' + candidate.wasteFt2 + this.areaUnit + ' ' + (this.labels.leftover || 'left over') + '</span>' +
        '</div>' +
        '<div class="jnet__option-buy">' +
          '<span class="jnet__option-price">' + this.money(candidate.totalPrice) + '</span>' +
          '<button type="button" class="jnet__choose' + (chosen ? ' is-chosen' : '') + '"' +
            ' data-net-choose="' + candidate.variantId + '"' +
            ' data-net-product="' + product.handle + '"' +
            ' data-net-group="' + group.key + '">' +
            (chosen ? (this.labels.chosen || 'Chosen') : (this.labels.chooseThis || 'Choose this')) +
          '</button>' +
        '</div>' +
      '</div>'
    );
  };

  Netting.prototype.othersHtml = function (product, others, group) {
    var html = '';
    for (var i = 0; i < others.length; i++) {
      html += this.optionHtml(product, others[i], false, group);
    }

    return (
      '<details class="jnet__others">' +
        '<summary>' + (this.labels.otherSizes || 'See other sizes that fit') + '</summary>' +
        html +
      '</details>'
    );
  };

  Netting.prototype.bindOptionButtons = function () {
    var self = this;
    var buttons = this.formatsEl.querySelectorAll('[data-net-choose]');

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', function (event) {
        var variantId = event.currentTarget.getAttribute('data-net-choose');
        var handle = event.currentTarget.getAttribute('data-net-product');
        var groupKey = event.currentTarget.getAttribute('data-net-group') || 'main';
        self.choose(groupKey, handle, variantId);
      });
    }
  };

  Netting.prototype.choose = function (groupKey, handle, variantId) {
    var group = null;
    for (var g = 0; g < this.requirement.groups.length; g++) {
      if (this.requirement.groups[g].key === groupKey) group = this.requirement.groups[g];
    }
    if (!group) return;

    for (var i = 0; i < this.config.products.length; i++) {
      var product = this.config.products[i];
      if (product.handle !== handle) continue;

      var fits = this.candidates(product, group);
      for (var c = 0; c < fits.length; c++) {
        if (String(fits[c].variantId) !== String(variantId)) continue;

        this.choices[groupKey] = {
          group: groupKey,
          groupLabel: group.label,
          colour: this.colour,
          colourLabel: this.colourLabel(this.colour),
          format: product.format,
          formatLabel: product.formatLabel,
          productTitle: product.title,
          productHandle: product.handle,
          productUrl: product.url,
          variantId: fits[c].variantId,
          sku: fits[c].sku,
          variantTitle: fits[c].variantTitle,
          unitPrice: fits[c].unitPrice,
          quantity: fits[c].quantity,
          totalPrice: fits[c].totalPrice,
          covers: { run: fits[c].suppliedRun, drop: fits[c].suppliedDrop },
          required: { run: group.run, drop: group.drop, area: group.area },
          wasteFt2: fits[c].wasteFt2
        };

        this.renderFormats();
        this.renderZips();
        this.report();
        return;
      }
    }
  };

  Netting.prototype.colourLabel = function (id) {
    for (var i = 0; i < this.config.colours.length; i++) {
      if (this.config.colours[i].id === id) return this.config.colours[i].label;
    }
    return id;
  };

  /**
   * Zip options for the ends the customer is enclosing.
   *
   * A complete wall net panel is a fixed size, so it is filtered out when an
   * end is larger than it in either direction. Zips fitted into your own
   * netting have no size limit and always stay.
   */
  /** Is a zip relevant for this area? Balcony and fenceline are not enclosures. */
  Netting.prototype.zipsApplyToArea = function (area) {
    var areas = this.config.zipAreas;
    if (!areas || !areas.length) return true; // unset means no restriction

    for (var i = 0; i < areas.length; i++) {
      if (String(areas[i]).trim() === area) return true;
    }
    return false;
  };

  Netting.prototype.zipOptions = function () {
    var measure = this.requirement && this.requirement.measure;
    if (!measure) return { options: [], oversize: false };

    // Skip zips entirely for areas that are not an enclosure.
    if (!this.zipsApplyToArea(measure.area)) return { options: [], oversize: false };

    var ends = [];
    if (measure.end1) ends.push(measure.end1);
    if (measure.end2) ends.push(measure.end2);

    var biggest = { width: 0, height: 0 };
    for (var e = 0; e < ends.length; e++) {
      if (ends[e].width > biggest.width) biggest.width = ends[e].width;
      if (ends[e].height > biggest.height) biggest.height = ends[e].height;
    }

    var options = [];
    var panelExcluded = false;

    for (var i = 0; i < this.config.zips.length; i++) {
      var zip = this.config.zips[i];

      if (!zip.available) continue;
      if (zip.colour && this.colour && zip.colour !== this.colour) continue;

      var limited = zip.maxWidth > 0 || zip.maxHeight > 0;

      if (limited && ends.length) {
        var tooWide = zip.maxWidth > 0 && biggest.width > zip.maxWidth;
        var tooTall = zip.maxHeight > 0 && biggest.height > zip.maxHeight;

        if (tooWide || tooTall) {
          panelExcluded = true;
          continue;
        }
      }

      options.push(zip);
    }

    return { options: options, oversize: panelExcluded, ends: ends.length };
  };

  Netting.prototype.renderZips = function () {
    if (!this.zipBlock || !this.zipsEl) return;

    if (!this.colour || !this.allChosen()) {
      this.zipBlock.hidden = true;
      return;
    }

    var result = this.zipOptions();

    if (!result.options.length) {
      this.zipBlock.hidden = true;
      return;
    }

    this.zipBlock.hidden = false;

    var html = '';
    for (var i = 0; i < result.options.length; i++) {
      var zip = result.options[i];
      var chosen = this.zip && String(this.zip.variantId) === String(zip.variantId);

      html +=
        '<button type="button" class="jnet__zip' + (chosen ? ' is-chosen' : '') + '"' +
        ' aria-pressed="' + (chosen ? 'true' : 'false') + '"' +
        ' data-net-zip="' + zip.variantId + '">' +
          (zip.image ? '<img class="jnet__zip-image" src="' + zip.image + '" alt="" loading="lazy">' : '') +
          '<span class="jnet__zip-tier">' + (zip.tierLabel || '') + '</span>' +
          '<span class="jnet__zip-title">' + zip.title + '</span>' +
          (zip.blurb ? '<span class="jnet__zip-blurb">' + zip.blurb + '</span>' : '') +
          '<span class="jnet__zip-price">' + this.money(zip.price) + '</span>' +
        '</button>';
    }

    var noneChosen = !this.zip;
    html +=
      '<button type="button" class="jnet__zip jnet__zip--none' + (noneChosen ? ' is-chosen' : '') + '"' +
      ' aria-pressed="' + (noneChosen ? 'true' : 'false') + '" data-net-zip="none">' +
        '<span class="jnet__zip-title">' + (this.labels.zipNone || 'No zip, thanks') + '</span>' +
      '</button>';

    // A chosen zip gets a quantity stepper: two enclosed ends usually means
    // two doors. Hidden when only one can go in anyway.
    if (this.zip && this.zipMax(this.zip) > 1) {
      var maxQ = this.zipMax(this.zip);
      var q = this.zip.quantity || 1;

      html +=
        '<div class="jnet__zip-qty">' +
          '<span class="jnet__zip-qty-label">' + (this.labels.zipQty || 'How many zips?') + '</span>' +
          '<button type="button" class="jnet__zip-qty-btn" data-net-zip-qty="minus"' +
            ' aria-label="Fewer"' + (q <= 1 ? ' disabled' : '') + '>&minus;</button>' +
          '<span class="jnet__zip-qty-value" data-net-zip-qty-value>' + q + '</span>' +
          '<button type="button" class="jnet__zip-qty-btn" data-net-zip-qty="plus"' +
            ' aria-label="More"' + (q >= maxQ ? ' disabled' : '') + '>+</button>' +
        '</div>';
    }

    this.zipsEl.innerHTML = html;

    if (this.zipNoteEl) {
      this.zipNoteEl.textContent = result.oversize ? (this.labels.zipTooBig || '') : '';
      this.zipNoteEl.hidden = !result.oversize;
    }

    var self = this;
    var buttons = this.zipsEl.querySelectorAll('[data-net-zip]');
    for (var b = 0; b < buttons.length; b++) {
      buttons[b].addEventListener('click', function (event) {
        self.chooseZip(event.currentTarget.getAttribute('data-net-zip'));
      });
    }

    var steppers = this.zipsEl.querySelectorAll('[data-net-zip-qty]');
    for (var q = 0; q < steppers.length; q++) {
      steppers[q].addEventListener('click', function (event) {
        self.setZipQuantity(event.currentTarget.getAttribute('data-net-zip-qty'));
      });
    }
  };

  Netting.prototype.setZipQuantity = function (dir) {
    if (!this.zip) return;

    var next = (this.zip.quantity || 1) + (dir === 'plus' ? 1 : -1);
    next = Math.max(1, Math.min(this.zipMax(this.zip), next));
    if (next === this.zip.quantity) return;

    this.zip.quantity = next;

    // Each panel covers one more end's netting, so quantity feeds straight
    // back into the requirement. For other zips this just re-renders and
    // re-reports.
    this.refresh();
  };

  Netting.prototype.chooseZip = function (variantId) {
    if (variantId === 'none') {
      this.zip = null;
    } else {
      for (var i = 0; i < this.config.zips.length; i++) {
        if (String(this.config.zips[i].variantId) !== String(variantId)) continue;

        var zip = this.config.zips[i];

        // Re-clicking the chosen zip keeps its quantity; switching resets.
        var sameAsBefore = this.zip && String(this.zip.variantId) === String(variantId);

        this.zip = {
          variantId: zip.variantId,
          sku: zip.sku,
          title: zip.title,
          tier: zip.tier,
          price: zip.price,
          quantity: sameAsBefore ? (this.zip.quantity || 1) : 1
        };
        break;
      }
    }

    // A panel zip changes how much netting is needed (it covers the end
    // itself), so the whole requirement re-evaluates. The chosen nets' lengths
    // and prices follow automatically via validateSelections.
    this.refresh();
  };

  /**
   * A chosen net for every group completes the step. A zip is optional and
   * never blocks it.
   *
   * The first net (the main run's) is mirrored at the top level of the data,
   * so everything written against the single-net shape (analytics, the 3D
   * preview) keeps working, while `nets` carries the full list and
   * `totalPrice` the combined netting spend.
   */
  Netting.prototype.report = function () {
    if (!this.journey) return;

    if (!this.allChosen()) {
      if (this.journey.data(this.stepId)) this.journey.clear(this.stepId);
      return;
    }

    var nets = [];
    var total = 0;

    for (var g = 0; g < this.requirement.groups.length; g++) {
      var choice = this.choices[this.requirement.groups[g].key];
      var net = {};
      for (var key in choice) net[key] = choice[key];
      nets.push(net);
      total += net.totalPrice;
    }

    var data = {};
    for (var k in nets[0]) data[k] = nets[0][k];
    data.nets = nets;
    data.totalPrice = total;
    data.zip = this.zip;

    this.journey.complete(this.stepId, data);
  };

  function boot() {
    var roots = document.querySelectorAll('[data-journey-step-source]');

    for (var i = 0; i < roots.length; i++) {
      if (roots[i].querySelector('[data-net-config]')) new Netting(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
